<template>
  <div>
    <div class="p-4 font-medium text-xs text-left leading-snug max-h-96 overflow-auto bg-charcoal-100 dark:bg-dark-950">
      <p class="font-bold text-2xl -mt-0.5">
        {{ $t('components.Event.validation.title') }}
      </p>
      <p class="text-base mt-0.5">{{ $t('components.Event.validation.content') }}</p>

      <div class="font-medium text-2xs mt-3 -mx-1">
        <span class="bg-charcoal-300/30 dark:bg-dark-300 dark:text-dark-950 text-charcoal-900 py-1 pl-1.5 pr-2.5 m-0.5 rounded-full inline-flex space-x-1 items-center"><IconBase name="cute/cross-fill" :width="15" /><p>{{ $t('components.Event.validation.states.noShow') }}</p></span>
        <span class="bg-charcoal-300/30 dark:bg-dark-300 dark:text-dark-950 text-charcoal-900 py-1 pl-1.5 pr-2.5 m-0.5 rounded-full inline-flex space-x-1 items-center"><IconBase name="cute/diamond-fill" :width="15" /><p>{{ $t('components.Event.validation.states.canceled') }}</p></span>
        <span class="bg-charcoal-300/30 dark:bg-dark-300 dark:text-dark-950 text-charcoal-900 py-1 pl-1.5 pr-2.5 m-0.5 rounded-full inline-flex space-x-1 items-center"><IconBase name="cute/check-fill" :width="15" /><p>{{ $t('components.Event.validation.states.checkedIn') }}</p></span>
      </div>
      
      <div class="space-y-2">
        <div class="bg-white dark:bg-dark-800 dark:text-dark-50 p-2 rounded-xl text-light-950 shadow mt-4 space-y-1.5" v-if="pendingUsers.length > 0">
          <div class="flex items-center" v-for="user in pendingUsers" :key="user._id">
            <div class="grow leading-tight flex items-center space-x-2">
              <UserPicture :user="user" class="shrink-0" size="base" is-link />

              <div class="shrink-0 min-w-20 space-y-0.5">
                <div class="text-base font-medium line-clamp-1 break-all">
                  {{ user.name }}
                </div>
                <div v-if="(event.getAttendance(user._id)?.guests ?? 0) > 0">
                  {{ $t('components.Event.steps.guests', event.getAttendance(user._id)?.guests ?? 0) }}
                </div>
              </div>
            </div>

            <ButtonToggleLabels
              class="shrink-0 w-36 !bg-charcoal-100/70 dark:!bg-dark-950"
              v-model="formData[user._id]"
              is-resettable
              :button="{
                size: '2xs'
              }"
              :options="[
                { icon: 'cute/cross-fill', value: 'no-show', button: { color: 'ruby', iconSize: 18, icon: 'cute/cross-fill', } },
                { icon: 'cute/diamond-fill', value: 'canceled', button: { color: 'royal', iconSize: 18, icon: 'cute/diamond-fill' } },
                { icon: 'cute/check-fill', value: 'checked-in', button: { color: 'emerald', iconSize: 18, icon: 'cute/check-fill', } },
              ]"
              @reset="() => delete formData[user._id]"
            />
          </div>
        </div>

        <div class="bg-white dark:bg-dark-800 dark:text-dark-50 p-3 rounded-xl text-light-950 shadow mt-4" v-if="doneUsers.length > 0">
          <p class="mb-1 -mt-0.5 font-bold">{{ $t('components.Event.validation.done') }}</p>
          <div class="flex flex-wrap">
            <div class="m-0.5 relative" v-for="user in doneUsers" :key="user._id">
              <UserPicture :user="user" is-hide-badges size="sm+" is-link />

              <div class="size-4 rounded-full flex items-center justify-center absolute -bottom-0.5 -right-0.5 text-white" :class="[ event.attendees.find(u => u._id === user._id)?.type === 'checked-in' ? 'bg-emerald-500' : (event.attendees.find(u => u._id === user._id)?.type === 'canceled' ? 'bg-royal-600' : 'bg-ruby-500') ]">
                <IconBase :name="event.attendees.find(u => u._id === user._id)?.type === 'checked-in' ? 'cute/check-fill' : (event.attendees.find(u => u._id === user._id)?.type === 'canceled' ? 'cute/diamond-fill' : 'cute/cross-fill')" :width="8" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="bg-white border-t border-charcoal-200 dark:bg-dark-800 dark:border-dark-800 flex items-center space-x-2 p-2.5">
      <ButtonBase style-type="round" class="shrink-0" color="white"  @click="() => emit('previous')">
        <IconBase name="cute/angle-right" class="rotate-180" :width="20" />
      </ButtonBase>

      <ButtonBase color="builders" is-full :is-loading="isLoading" :is-disabled="Object.keys(formData).length <= 0" :count="Object.keys(formData).length > 0 ? Object.keys(formData).length : undefined" @click="onNext">
        {{ $t('actions.submit') }}
      </ButtonBase>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Event, EventAttendeeState } from '@/classes'
import EventStore from '@/store/events'
import UserStore from '@/store/user'
import UsersStore from '@/store/users'

const props = withDefaults(defineProps<{
  event: Event
}>(), {

})

const { event } = toRefs(props)

const eventStore = EventStore()
const usersStore = UsersStore()
const userStore = UserStore()
const { currentUser } = storeToRefs(userStore)

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'previous'): void
  (e: 'reset'): void
}>()

const formData: Ref<{ [key: string]: typeof EventAttendeeState[number]}> = ref({})

const pendingUsers = computed(() => usersStore.getItems(event.value.attendees.filter(a => a.type === 'attending').map(a => a._id)))

const doneUsers = computed(() => usersStore.getItems(event.value.attendees.filter(a => a.type !== 'attending').map(a => a._id)))

const isLoading = ref(false)

const onNext = async () => {
  isLoading.value = true
  
  await eventStore.attendSet(event.value._id, formData.value)

  formData.value = {}

  isLoading.value = false
}

</script>